import axios, { type AxiosError, type AxiosResponse } from "axios";
import dayjs from "dayjs";
import { jwtDecode } from "jwt-decode";

import { useAuthStore } from "@krea/shared/features/auth/authStore";

import { refreshTokens } from "./features/auth/api";
import type { IAuthData } from "./features/auth/types";

let ongoingRefreshRequest: Promise<IAuthData> | null = null;

const refreshAndSetTokens = async (token: string | null = null) => {
  const { refreshToken, setAccessToken, setRefreshToken } =
    useAuthStore.getState();
  let refreshTokenToUse: string | null = token;

  if (!refreshTokenToUse) {
    refreshTokenToUse = refreshToken;
  }

  if (!refreshTokenToUse) {
    return null;
  }

  if (!ongoingRefreshRequest) {
    ongoingRefreshRequest = refreshTokens({ refreshToken: refreshTokenToUse });
  }

  try {
    const response = await ongoingRefreshRequest;

    setAccessToken(response.accessToken);
    setRefreshToken(response.refreshToken);

    return response.accessToken;
  } catch (error) {
    console.error("Failed to refresh tokens", error);
  } finally {
    ongoingRefreshRequest = null;
  }

  return null;
};

const checkAccessAndRefreshTokens = () => {
  const {
    accessToken: storedAccessToken,
    refreshToken: storedRefreshToken,
    reset,
  } = useAuthStore.getState();

  if (!storedAccessToken || !storedRefreshToken) {
    console.warn("Not logged in");

    return null;
  }
  const decodedAccessToken = jwtDecode(storedAccessToken);
  const decodedRefreshToken = jwtDecode(storedRefreshToken);

  const hasAccessTokenExpired =
    decodedAccessToken.exp && decodedAccessToken.exp - dayjs().unix() <= 0;
  const hasRefreshTokenExpired =
    decodedRefreshToken.exp && decodedRefreshToken.exp - dayjs().unix() <= 0;

  if (hasRefreshTokenExpired) {
    console.warn("Refresh token has expired");
    reset();
    window.location.href = "/login";

    return null;
  }

  if (hasAccessTokenExpired) {
    return refreshAndSetTokens(storedRefreshToken);
  }

  return storedAccessToken;
};

export const getAccessToken = async () => {
  const currentURL = new URL(window.location.href);

  // quickAuth could contain a very short-lived refreshToken so that we can "quickAuth" into customer web from
  // other authenticated sources.
  const quickAuthRefreshToken = currentURL.searchParams.get("quickAuth");

  currentURL.searchParams.delete("quickAuth");

  window.history.replaceState(null, "", currentURL.href);

  if (quickAuthRefreshToken) {
    try {
      const decodedQuickAuthToken = jwtDecode(quickAuthRefreshToken);

      const quickAuthTokenExpired =
        decodedQuickAuthToken.exp &&
        decodedQuickAuthToken.exp - dayjs().unix() <= 0;

      if (!quickAuthTokenExpired) {
        // If we have a quickAuthRefreshToken.
        // We want to use  that to get a fresh set of "normal" tokens.
        return refreshAndSetTokens(quickAuthRefreshToken);
      }
    } catch (err) {
      console.error("Failed to decode quickAuth token", err);
    }
  }

  return checkAccessAndRefreshTokens();
};

export class HttpService {
  ongoingRefreshRequest: Promise<AxiosResponse> | null = null;

  static instance: HttpService | null;

  constructor() {
    this.ongoingRefreshRequest = null;
  }

  static getInstance() {
    if (HttpService.instance) {
      return HttpService.instance;
    }
    HttpService.instance = new HttpService();

    return HttpService.instance;
  }

  setupInterceptors() {
    axios.interceptors.request.use(
      async (config) => {
        const currentAccessToken = await getAccessToken();
        config.headers.Authorization = currentAccessToken
          ? `Bearer ${currentAccessToken}`
          : null;

        return config;
      },
      (error) => {
        // Do something with request error
        return Promise.reject(error as Error);
      },
    );
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          useAuthStore.getState().reset();

          window.location.href = "/login";

          return Promise.reject(new Error("Not authenticated"));
        }
        return Promise.reject(error);
      },
    );
  }
}
